import React from 'react';
import { Flex, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import Avatar from '@atlaskit/avatar';
import { ValueType } from '@atlaskit/select';
// @ts-ignore
import UserIcon from '@css-xp/base/assets/img/application/icon-user.svg';
import { ContactInfo, InvoiceGroup, PaymentMethod, CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { formatAddressLabel } from '@css-xp/base';
import { splitAndCapitalize } from '@css-xp/ppc-utils';
import { CustomerContactsAvatarGroup } from '@css-xp/ppc-common';
import { BillingProfileStyles } from './billing-profile-panel.styles';

const styledSelectedAppContainer = xcss({
  display: 'flex',
  gap: 'space.075',
  width: '100%',
});

export interface BillingProfileOption {
  label: string;
  value: string;
}

interface Props {
  billingProfile: InvoiceGroup | null | undefined;
  selectedBillingProfile?: ValueType<BillingProfileOption> | null;
  setSelectedBillingProfile?: (selected: ValueType<BillingProfileOption> | null) => void;
  isDisabled?: boolean; // need to check
  showBillingProfileId?: boolean;
  showPricingEligibility?: boolean;
  disableReason?: string;
  showPrimaryContacts?: boolean;
  primaryCustomerContact?: ContactInfo;
  isMultiCurrencyChangesEnabled?: boolean;
  isPrimaryContactsLoading?: boolean;
}

export const BillingProfilePanel = ({
  billingProfile,
  selectedBillingProfile,
  setSelectedBillingProfile,
  showPricingEligibility,
  disableReason,
  showPrimaryContacts,
  primaryCustomerContact,
  isMultiCurrencyChangesEnabled,
  isPrimaryContactsLoading,
  isDisabled,
}: Props) => {
  const getDefaultPaymentSection = (defaultPaymentMethod?: PaymentMethod | null) => {
    if (!defaultPaymentMethod) {
      return <p className="billing-profile-no-payment-method">No payment method found</p>;
    }

    const defaultPaymentMethodObj = defaultPaymentMethod as any;
    return (
      <PaymentMethodPanel
        paymentMethod={{
          id: defaultPaymentMethodObj.id as PaymentMethodId,
          type: defaultPaymentMethodObj.type,
          card: {
            brand: defaultPaymentMethodObj.card?.brand,
            expMonth: defaultPaymentMethodObj.card?.expirationMonth,
            expYear: defaultPaymentMethodObj.card?.expirationYear,
            last4: defaultPaymentMethodObj.card?.last4Numbers,
          },
          default: defaultPaymentMethod?.default ?? false,
        }}
      />
    );
  };

  const isPricingEligibilityEnabled = showPricingEligibility && !!billingProfile?.shipToPriceEligibility;
  const isMultiCurrencyEnabled = isMultiCurrencyChangesEnabled && !!billingProfile?.currency;

  const isMultiCurrencyAndPricingEligibilityEnabled = isPricingEligibilityEnabled && isMultiCurrencyEnabled;

  const renderPrimaryContactInfoPanel = () => {
    if (isPrimaryContactsLoading) {
      return (
        <SkeletonLoaderWrapper>
          <SkeletonLoader circle={true} width="25%" height="25px" />
        </SkeletonLoaderWrapper>
      );
    }

    if (primaryCustomerContact) {
      return (
        <CustomerContactsAvatarGroup
          contacts={[primaryCustomerContact] as any}
          avatarSize="small"
          showContactType={true}
        />
      );
    }
    return (
      <Tooltip content="Unassigned (Primary)">
        <Avatar src={UserIcon.toString()} testId="unassigned-primary-contact" size="small" />
      </Tooltip>
    );
  };

  return (
    <div>
      <BillingProfileStyles
        {...{
          isClickable: !isDisabled,
          isSelected: selectedBillingProfile?.value === billingProfile?.id,
          ...(!isDisabled && {
            onClick: () => {
              const selected = {
                label: billingProfile?.name || '',
                value: billingProfile?.id || '',
              };
              setSelectedBillingProfile?.(selected);
            },
          }),
        }}
      >
        <Flex xcss={styledSelectedAppContainer}>
          <div className="billing-profile-info">
            <div className="billing-profile-header">
              <div className="billing-profile-info-title" title={billingProfile?.name ?? ''}>
                {billingProfile?.name}
              </div>
              {showPrimaryContacts && (
                <div className="billing-profile-contact-info">{renderPrimaryContactInfoPanel()}</div>
              )}
            </div>
            <div className="billing-profile-price-eligibility">
              {isPricingEligibilityEnabled ? splitAndCapitalize(billingProfile?.shipToPriceEligibility) : 'Commercial'}
              {isMultiCurrencyAndPricingEligibilityEnabled && ' | '}
              {isMultiCurrencyEnabled && billingProfile?.currency}
            </div>
            <div className="billing-profile-info-payment">
              {getDefaultPaymentSection(billingProfile?.defaultPaymentMethod)}
            </div>
            {billingProfile?.shipToParty && (
              <div className="billing-profile-info-ship-to-party">
                {formatAddressLabel(billingProfile?.shipToParty as CommerceAddress) || ''}
              </div>
            )}
            {disableReason && (showPricingEligibility || isMultiCurrencyChangesEnabled) && (
              <div className="billing-profile-disable-reason">{disableReason}</div>
            )}
          </div>
        </Flex>
      </BillingProfileStyles>
    </div>
  );
};
